import { useEffect, useState } from 'react';
import api from '../utils/api';
import { formatearMonto, generarClave, ETIQUETAS_METODO, ETIQUETAS_MOTIVO } from '../utils/formato';
import { IconPersona, IconDocumento, IconDinero, IconAdvertencia } from './icons';

const MOTIVOS = ['emision', 'renovacion', 'reimpresion'];
const METODOS = ['efectivo', 'tarjeta', 'transferencia'];

function aNumero(texto) {
  return Number(String(texto).trim().replace(',', '.'));
}

/**
 * Carga de un cobro a mano. Dos pasos: formulario y una pantalla de "revisá los datos"
 * antes de registrar (un cobro no se edita después: si hay un error, se anula).
 *
 * La clave de idempotencia se genera una sola vez al abrir el formulario: si el operador
 * toca "Registrar" dos veces, o se corta la conexión y reintenta, no se duplica el cobro.
 */
function NuevoCobroModal({ onCerrar, onRegistrado }) {
  const [clave] = useState(generarClave);
  const [paso, setPaso] = useState('formulario');
  const [contribuyente, setContribuyente] = useState('');
  const [dni, setDni] = useState('');
  const [motivo, setMotivo] = useState('');
  const [monto, setMonto] = useState('');
  const [metodo, setMetodo] = useState('');
  const [referencia, setReferencia] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    function alTeclear(evento) {
      if (evento.key === 'Escape' && !enviando) onCerrar();
    }
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [onCerrar, enviando]);

  // El aviso de error se va apenas se edita cualquier campo (no queda un reclamo viejo
  // con el formulario ya corregido). Un error del servidor sigue visible hasta que se toque algo.
  useEffect(() => {
    setError('');
  }, [contribuyente, dni, motivo, monto, metodo, referencia]);

  const conReferencia = metodo === 'tarjeta' || metodo === 'transferencia';

  function revisar(evento) {
    evento.preventDefault();
    setError('');

    if (contribuyente.trim().length < 2) {
      return setError('Escribí el nombre del contribuyente.');
    }
    const dniLimpio = dni.replace(/[.\s]/g, '');
    if (dniLimpio !== '' && !/^\d{6,9}$/.test(dniLimpio)) {
      return setError('El DNI tiene que tener entre 6 y 9 números (o dejalo vacío).');
    }
    if (!motivo) return setError('Elegí el tipo de trámite.');
    const montoNumero = aNumero(monto);
    if (!/^\d+([.,]\d{1,2})?$/.test(monto.trim()) || !(montoNumero > 0)) {
      return setError('Escribí un monto mayor a cero, con hasta 2 decimales.');
    }
    if (!metodo) return setError('Elegí el método de pago.');
    setPaso('resumen');
  }

  async function registrar() {
    setError('');
    setEnviando(true);
    try {
      const { data } = await api.post('/cobros/manual', {
        contribuyente: contribuyente.trim(),
        dni: dni.trim() || undefined,
        motivo,
        monto: aNumero(monto),
        metodo_pago: metodo,
        referencia: conReferencia ? referencia.trim() || undefined : undefined,
        clave_idempotencia: clave,
      });
      onRegistrado(data.cobro);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo registrar el cobro. Revisá tu conexión y reintentá.');
      setPaso('formulario');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="modal-fondo no-imprimir" role="dialog" aria-modal="true" aria-label="Nuevo cobro">
      <div className="modal-caja modal-caja-ancha">
        {paso === 'formulario' && (
          <form onSubmit={revisar} noValidate>
            <h2>Nuevo cobro</h2>
            <p className="modal-ayuda">Se registra en tu caja abierta. Completá los datos del trámite.</p>

            {error && (
              <p className="mensaje-error">
                <IconAdvertencia size={16} />
                {error}
              </p>
            )}

            <div className="form-group">
              <label htmlFor="cobro_contribuyente">Nombre del contribuyente</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <IconPersona size={18} />
                </span>
                <input
                  id="cobro_contribuyente"
                  className="form-input"
                  type="text"
                  value={contribuyente}
                  onChange={(e) => setContribuyente(e.target.value)}
                  maxLength={120}
                  autoComplete="off"
                  autoFocus
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="cobro_dni">DNI (opcional)</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <IconDocumento size={18} />
                </span>
                <input
                  id="cobro_dni"
                  className="form-input"
                  type="text"
                  inputMode="numeric"
                  value={dni}
                  onChange={(e) => setDni(e.target.value)}
                  maxLength={12}
                  autoComplete="off"
                />
              </div>
            </div>

            <fieldset className="grupo-opciones">
              <legend>Tipo de trámite</legend>
              <div className="opciones-segmentadas">
                {MOTIVOS.map((m) => (
                  <label key={m} className={`opcion-segmentada ${motivo === m ? 'activa' : ''}`}>
                    <input type="radio" name="motivo" value={m} checked={motivo === m} onChange={() => setMotivo(m)} />
                    {ETIQUETAS_MOTIVO[m]}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="form-group">
              <label htmlFor="cobro_monto">Monto a cobrar</label>
              <div className="input-wrapper">
                <span className="input-icon">
                  <IconDinero size={18} />
                </span>
                <input
                  id="cobro_monto"
                  className="form-input"
                  type="number"
                  inputMode="decimal"
                  min="0.01"
                  max="10000000"
                  step="0.01"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                />
              </div>
            </div>

            <fieldset className="grupo-opciones">
              <legend>Método de pago</legend>
              <div className="opciones-segmentadas">
                {METODOS.map((m) => (
                  <label key={m} className={`opcion-segmentada ${metodo === m ? 'activa' : ''}`}>
                    <input type="radio" name="metodo" value={m} checked={metodo === m} onChange={() => setMetodo(m)} />
                    {ETIQUETAS_METODO[m]}
                  </label>
                ))}
              </div>
            </fieldset>

            {conReferencia && (
              <div className="form-group">
                <label htmlFor="cobro_referencia">Referencia (opcional)</label>
                <input
                  id="cobro_referencia"
                  type="text"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  maxLength={128}
                  autoComplete="off"
                />
                <p className="campo-ayuda">
                  {metodo === 'tarjeta'
                    ? 'Número de cupón del posnet. No cargues el número de la tarjeta.'
                    : 'Número de operación o de comprobante de la transferencia.'}
                </p>
              </div>
            )}

            <div className="modal-acciones">
              <button type="button" className="boton boton-secundario" onClick={onCerrar}>
                Cancelar
              </button>
              <button type="submit" className="boton boton-primario">
                Continuar
              </button>
            </div>
          </form>
        )}

        {paso === 'resumen' && (
          <div>
            <h2>Revisá el cobro</h2>
            <p className="modal-ayuda">
              Una vez registrado, el cobro no se puede editar. Si hay un error, después se anula y se carga de nuevo.
            </p>

            <dl className="resumen-cobro">
              <div>
                <dt>Contribuyente</dt>
                <dd>{contribuyente.trim()}</dd>
              </div>
              {dni.trim() && (
                <div>
                  <dt>DNI</dt>
                  <dd>{dni.trim()}</dd>
                </div>
              )}
              <div>
                <dt>Trámite</dt>
                <dd>{ETIQUETAS_MOTIVO[motivo]}</dd>
              </div>
              <div>
                <dt>Método de pago</dt>
                <dd>{ETIQUETAS_METODO[metodo]}</dd>
              </div>
              {conReferencia && referencia.trim() && (
                <div>
                  <dt>Referencia</dt>
                  <dd>{referencia.trim()}</dd>
                </div>
              )}
            </dl>
            <p className="resumen-cobro-monto">{formatearMonto(aNumero(monto))}</p>

            <div className="modal-acciones">
              <button type="button" className="boton boton-secundario" onClick={() => setPaso('formulario')} disabled={enviando}>
                Volver a editar
              </button>
              <button type="button" className="boton boton-primario" onClick={registrar} disabled={enviando}>
                {enviando ? 'Registrando…' : 'Registrar cobro'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default NuevoCobroModal;
